import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  makeStyles,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Popover,
  Divider,
} from "@material-ui/core";
import { ThreeDots } from "react-loader-spinner";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import DeleteIcon from "@material-ui/icons/Delete";
import WarningIcon from "@material-ui/icons/Warning";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AuthContext from "connections/auth/AuthProvider.js";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import FilterSystem from "../../sharedComponents/FilterSystem/FilterSystem.js";
import { getFilterOptions } from "../../viewServices/calls/apiCalls.js";

// Mapping from mask dimension IDs to standard filter keys
const DIMENSION_TO_FILTER_KEY = {
  region_reference: "regions",
  sku_class01: "brands",
  sku_name: "products",
  channel_reference: "channels",
  node2_reference_client: "clients",
  solver_status: "solver_status",
};

const useStyles = makeStyles((theme) => ({
  ...customSelectStyle(theme),
  ruleCard: (props) => ({
    marginTop: 0,
    marginBottom: "5px",
    border: `1px solid ${grayColor[8]}`,
    borderLeft: `4px solid ${props.clientColor}`,
    borderRadius: theme.spacing(1),
  }),
  ruleHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    backgroundColor: grayColor[12],
    cursor: "pointer",
    "&:hover": {
      backgroundColor: grayColor[11],
    },
  },
  ruleTitle: (props) => ({
    fontWeight: 500,
    fontSize: "0.95rem",
    color: props.clientColor,
  }),
  ruleSummary: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(2),
  },
  ruleContent: {
    padding: theme.spacing(2),
  },
  selectorControl: (props) => ({
    marginBottom: theme.spacing(2),
    minWidth: "100%",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: `${grayColor[4]}!important`,
      },
      "&:after": {
        borderBottomColor: `${props.clientColor}!important`,
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: `${props.clientColor}!important`,
      },
    },
  }),
  filterRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
  },
  filterDimension: {
    minWidth: "150px",
    fontWeight: 500,
  },
  filterValues: {
    flex: 1,
  },
  operationRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
  operationSelect: (props) => ({
    minWidth: "200px",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: `${grayColor[4]}!important`,
      },
      "&:after": {
        borderBottomColor: `${props.clientColor}!important`,
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: `${props.clientColor}!important`,
      },
    },
  }),
  valueInput: (props) => ({
    width: "150px",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: `${grayColor[4]}!important`,
      },
      "&:after": {
        borderBottomColor: `${props.clientColor}!important`,
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: `${props.clientColor}!important`,
      },
    },
  }),
  addRuleButton: (props) => ({
    marginTop: theme.spacing(2),
    width: "100%",
    borderColor: grayColor[6],
    color: props.clientColor,
    "&:hover": {
      borderColor: grayColor[4],
      backgroundColor: `${props.clientColor}10`,
    },
  }),
  filterSelect: (props) => ({
    marginTop: theme.spacing(1),
    minWidth: 200,
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: `${grayColor[4]}!important`,
      },
      "&:after": {
        borderBottomColor: `${props.clientColor}!important`,
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: `${props.clientColor}!important`,
      },
    },
  }),
  sectionLabel: (props) => ({
    fontSize: "0.875rem",
    fontWeight: 500,
    color: props.clientColor,
    marginBottom: theme.spacing(1),
  }),
  dimensionValueInput: (props) => ({
    width: "70px",
    "& .MuiInput-underline:before": {
      borderBottomWidth: "1px !important",
      borderBottomColor: `${grayColor[4]}!important`,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: `${props.clientColor}!important`,
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomColor: `${grayColor[4]}!important`,
    },
  }),
}));

const MaskRulesBuilder = ({
  rules = [],
  onChange,
  config,
  filterOptions = {},
  loadingFilters = false,
  scenarioId,
  requireScenario = false,
}) => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#1976d2";
  const classes = useStyles({ clientColor });
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.mask.rules",
  });
  const [expandedRules, setExpandedRules] = useState({});

  // Expand by dimension state
  const [expandByDimension, setExpandByDimension] = useState({}); // ruleIndex -> boolean
  const [selectedExpandDimension, setSelectedExpandDimension] = useState({}); // ruleIndex -> dimension key
  const [dimensionValues, setDimensionValues] = useState({}); // ruleIndex -> array of distinct values
  const [dimensionValueInputs, setDimensionValueInputs] = useState({}); // ruleIndex -> {value: number}
  const [dimensionAggregates, setDimensionAggregates] = useState({}); // ruleIndex -> {dimVal: {avg, min, max, has_variance}}
  const [dimensionSamples, setDimensionSamples] = useState({}); // ruleIndex -> {dimVal: [raw values]}
  const [loadingDimensionValues, setLoadingDimensionValues] = useState({}); // ruleIndex -> boolean
  const [currentPage, setCurrentPage] = useState({}); // ruleIndex -> page number
  const [variancePopover, setVariancePopover] = useState({
    anchorEl: null,
    ruleIndex: null,
    dimVal: null,
  }); // Popover state

  // Extract available parameters and dimensions from config
  const availableParameters = config?.parameters || [];
  const availableDimensions = config?.dimensions || [];

  // Convert mask config dimensions to FilterSystem format
  // FilterSystem expects { key, label } where key matches filterOptions keys
  const filterDimensions = availableDimensions.map((dim) => ({
    key: DIMENSION_TO_FILTER_KEY[dim.id] || dim.id,
    label: dim.label,
  }));

  // Pagination constants
  const ITEMS_PER_PAGE = 10;

  // Watch for filter changes and re-fetch dimension values if expand is active
  // Create separate effect for each rule to avoid unnecessary re-renders
  useEffect(() => {
    const refetchForRule = async (ruleIndex) => {
      const rule = rules[ruleIndex];

      // Only refetch if this rule has expand active and a dimension selected
      if (
        !expandByDimension[ruleIndex] ||
        !selectedExpandDimension[ruleIndex] ||
        !scenarioId ||
        !rule
      ) {
        return;
      }

      setLoadingDimensionValues((prev) => ({
        ...prev,
        [ruleIndex]: true,
      }));

      try {
        const ruleFilters = rule.filters || {};
        const dimensionKey = selectedExpandDimension[ruleIndex];

        console.log(
          `🔄 Re-fetching ${dimensionKey} (rule ${ruleIndex}) with filters:`,
          ruleFilters,
        );
        console.log(
          `📡 API call: getFilterOptions(${scenarioId}, ${JSON.stringify(ruleFilters)}, "${dimensionKey}")`,
        );

        const response = await getFilterOptions(
          scenarioId,
          ruleFilters,
          dimensionKey,
          rule.parameter, // Pass parameter for aggregation
        );

        console.log(`📦 API Response:`, response);
        const values = response[dimensionKey] || [];
        const aggregates = response.aggregates || {};
        const samples = response.samples || {};
        console.log(
          `✅ Re-fetched ${values.length} filtered values for ${dimensionKey}:`,
          values.slice(0, 5), // Show first 5 values
        );

        setDimensionValues((prev) => ({ ...prev, [ruleIndex]: values }));
        setDimensionAggregates((prev) => ({
          ...prev,
          [ruleIndex]: aggregates,
        }));
        setDimensionSamples((prev) => ({
          ...prev,
          [ruleIndex]: samples,
        }));
        setCurrentPage((prev) => ({ ...prev, [ruleIndex]: 1 }));

        // Clear user inputs that are no longer in the filtered list
        setDimensionValueInputs((prev) => {
          const currentInputs = prev[ruleIndex] || {};
          const filteredInputs = {};
          Object.keys(currentInputs).forEach((key) => {
            if (values.includes(key)) {
              filteredInputs[key] = currentInputs[key];
            }
          });
          return { ...prev, [ruleIndex]: filteredInputs };
        });
      } catch (error) {
        console.error("❌ Failed to re-fetch dimension values:", error);
      } finally {
        setLoadingDimensionValues((prev) => ({
          ...prev,
          [ruleIndex]: false,
        }));
      }
    };

    // Refetch for all rules that have expand active
    rules.forEach((_, index) => {
      if (expandByDimension[index] && selectedExpandDimension[index]) {
        refetchForRule(index);
      }
    });
  }, [
    JSON.stringify(rules.map((r) => r.filters)),
    JSON.stringify(rules.map((r) => r.parameter)),
    scenarioId,
    expandByDimension,
    selectedExpandDimension,
  ]);

  const handleAddRule = () => {
    const newRule = {
      parameter: "",
      filters: {},
      operation: "increment_percentage",
      value: 0,
    };
    const updatedRules = [...rules, newRule];
    onChange(updatedRules);
    // Expand the new rule
    setExpandedRules({ ...expandedRules, [rules.length]: true });
  };

  const handleRemoveRule = (index) => {
    const updatedRules = rules.filter((_, i) => i !== index);
    onChange(updatedRules);

    // Clean up expand state for removed rule
    const newExpandByDimension = { ...expandByDimension };
    const newSelectedExpandDimension = { ...selectedExpandDimension };
    const newDimensionValues = { ...dimensionValues };
    const newDimensionValueInputs = { ...dimensionValueInputs };
    const newLoadingDimensionValues = { ...loadingDimensionValues };
    const newCurrentPage = { ...currentPage };
    const newExpandedRules = { ...expandedRules };

    // Remove state for deleted index
    delete newExpandByDimension[index];
    delete newSelectedExpandDimension[index];
    delete newDimensionValues[index];
    delete newDimensionValueInputs[index];
    delete newLoadingDimensionValues[index];
    delete newCurrentPage[index];
    delete newExpandedRules[index];

    // Shift indices down for rules after the deleted one
    Object.keys(newExpandByDimension).forEach((key) => {
      const idx = parseInt(key);
      if (idx > index) {
        newExpandByDimension[idx - 1] = newExpandByDimension[idx];
        delete newExpandByDimension[idx];
      }
    });

    Object.keys(newSelectedExpandDimension).forEach((key) => {
      const idx = parseInt(key);
      if (idx > index) {
        newSelectedExpandDimension[idx - 1] = newSelectedExpandDimension[idx];
        delete newSelectedExpandDimension[idx];
      }
    });

    Object.keys(newDimensionValues).forEach((key) => {
      const idx = parseInt(key);
      if (idx > index) {
        newDimensionValues[idx - 1] = newDimensionValues[idx];
        delete newDimensionValues[idx];
      }
    });

    Object.keys(newDimensionValueInputs).forEach((key) => {
      const idx = parseInt(key);
      if (idx > index) {
        newDimensionValueInputs[idx - 1] = newDimensionValueInputs[idx];
        delete newDimensionValueInputs[idx];
      }
    });

    Object.keys(newLoadingDimensionValues).forEach((key) => {
      const idx = parseInt(key);
      if (idx > index) {
        newLoadingDimensionValues[idx - 1] = newLoadingDimensionValues[idx];
        delete newLoadingDimensionValues[idx];
      }
    });

    Object.keys(newCurrentPage).forEach((key) => {
      const idx = parseInt(key);
      if (idx > index) {
        newCurrentPage[idx - 1] = newCurrentPage[idx];
        delete newCurrentPage[idx];
      }
    });

    Object.keys(newExpandedRules).forEach((key) => {
      const idx = parseInt(key);
      if (idx > index) {
        newExpandedRules[idx - 1] = newExpandedRules[idx];
        delete newExpandedRules[idx];
      }
    });

    // Update all state
    setExpandByDimension(newExpandByDimension);
    setSelectedExpandDimension(newSelectedExpandDimension);
    setDimensionValues(newDimensionValues);
    setDimensionValueInputs(newDimensionValueInputs);
    setLoadingDimensionValues(newLoadingDimensionValues);
    setCurrentPage(newCurrentPage);
    setExpandedRules(newExpandedRules);
  };

  const handleToggleExpand = (index) => {
    setExpandedRules({
      ...expandedRules,
      [index]: !expandedRules[index],
    });
  };

  const handleUpdateRule = (index, field, value) => {
    const updatedRules = [...rules];
    updatedRules[index] = {
      ...updatedRules[index],
      [field]: value,
    };
    onChange(updatedRules);
  };

  // Expand by dimension handlers
  const handleToggleExpandByDimension = async (ruleIndex, checked) => {
    setExpandByDimension({ ...expandByDimension, [ruleIndex]: checked });

    if (!checked) {
      // Clear expand-related state
      setSelectedExpandDimension({
        ...selectedExpandDimension,
        [ruleIndex]: null,
      });
      setDimensionValues({ ...dimensionValues, [ruleIndex]: [] });
      setDimensionValueInputs({ ...dimensionValueInputs, [ruleIndex]: {} });
      setCurrentPage({ ...currentPage, [ruleIndex]: 1 });

      // Update rule to remove expand fields
      const updatedRules = [...rules];
      delete updatedRules[ruleIndex].expand_by_dimension;
      delete updatedRules[ruleIndex].dimension_values;
      onChange(updatedRules);
    }
  };

  const handleExpandDimensionSelect = async (
    ruleIndex,
    dimensionKey,
    scenarioId,
  ) => {
    setSelectedExpandDimension({
      ...selectedExpandDimension,
      [ruleIndex]: dimensionKey,
    });
    setLoadingDimensionValues({ ...loadingDimensionValues, [ruleIndex]: true });
    setCurrentPage({ ...currentPage, [ruleIndex]: 1 });

    try {
      // Get rule filters
      const ruleFilters = rules[ruleIndex].filters || {};

      // Fetch dimension values filtered by current rule filters
      console.log(
        `🔍 Fetching values for ${dimensionKey} with filters:`,
        ruleFilters,
      );
      const response = await getFilterOptions(
        scenarioId,
        ruleFilters,
        dimensionKey,
        rules[ruleIndex].parameter, // Pass parameter for aggregation
      );

      const values = response[dimensionKey] || [];
      const aggregates = response.aggregates || {};
      const samples = response.samples || {};
      console.log(`✅ Got ${values.length} values for ${dimensionKey}`);
      console.log(`📊 Aggregates for rule ${ruleIndex}:`, aggregates);
      console.log(`📊 Samples for rule ${ruleIndex}:`, samples);

      setDimensionValues({ ...dimensionValues, [ruleIndex]: values });
      setDimensionAggregates({
        ...dimensionAggregates,
        [ruleIndex]: aggregates,
      });
      setDimensionSamples({
        ...dimensionSamples,
        [ruleIndex]: samples,
      });

      // Initialize inputs as empty - user must explicitly enter values
      const inputs = {};
      setDimensionValueInputs({ ...dimensionValueInputs, [ruleIndex]: inputs });

      // Update rule structure with empty dimension_values
      const updatedRules = [...rules];
      updatedRules[ruleIndex].expand_by_dimension = dimensionKey;
      updatedRules[ruleIndex].dimension_values = {};
      onChange(updatedRules);
    } catch (error) {
      console.error("❌ Failed to fetch dimension values:", error);
      setDimensionValues({ ...dimensionValues, [ruleIndex]: [] });
    } finally {
      setLoadingDimensionValues({
        ...loadingDimensionValues,
        [ruleIndex]: false,
      });
    }
  };

  const handleDimensionValueChange = (ruleIndex, dimValue, inputValue) => {
    const updatedInputs = {
      ...(dimensionValueInputs[ruleIndex] || {}),
    };

    // Only add value if user entered something (not empty)
    if (inputValue !== "" && inputValue !== null && inputValue !== undefined) {
      updatedInputs[dimValue] = parseFloat(inputValue) || 0;
    } else {
      // Remove the key if user clears the field
      delete updatedInputs[dimValue];
    }

    setDimensionValueInputs({
      ...dimensionValueInputs,
      [ruleIndex]: updatedInputs,
    });

    // Update rule - only include non-empty values
    const updatedRules = [...rules];
    updatedRules[ruleIndex].dimension_values = updatedInputs;
    onChange(updatedRules);
  };

  // Variance popover handlers
  const handleVariancePopoverOpen = (event, ruleIndex, dimVal) => {
    setVariancePopover({ anchorEl: event.currentTarget, ruleIndex, dimVal });
  };

  const handleVariancePopoverClose = () => {
    setVariancePopover({ anchorEl: null, ruleIndex: null, dimVal: null });
  };

  const getRuleSummary = (rule) => {
    if (!rule.parameter) return t("configure_parameter");

    const paramConfig = availableParameters.find(
      (p) => p.parameter === rule.parameter,
    );
    const paramLabel = paramConfig?.label || rule.parameter;

    const operationLabels = {
      increment_percentage: t("operations.increment_percentage"),
      increment_nominal: t("operations.increment_nominal"),
      set: t("operations.set"),
    };

    return `${operationLabels[rule.operation] || rule.operation}  ${paramLabel}`;
  };

  const getParameterOperations = (parameter) => {
    const paramConfig = availableParameters.find(
      (p) => p.parameter === parameter,
    );
    return (
      paramConfig?.operations || [
        "increment_percentage",
        "increment_nominal",
        "set",
      ]
    );
  };

  return (
    <Box>
      {/* Rules List */}
      {rules.map((rule, index) => {
        const isExpanded = expandedRules[index] || false;
        const paramOperations = getParameterOperations(rule.parameter);

        return (
          <Card key={index} className={classes.ruleCard}>
            {/* Rule Header */}
            <Box
              className={classes.ruleHeader}
              onClick={() => handleToggleExpand(index)}
            >
              <Box display="flex" alignItems="center" flex={1}>
                <Typography className={classes.ruleTitle}>
                  {t("rule")} {index + 1}
                </Typography>
                {!isExpanded && (
                  <Typography className={classes.ruleSummary}>
                    {getRuleSummary(rule)}
                  </Typography>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveRule(index);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
            </Box>

            {/* Rule Content (Expandable) */}
            <Collapse in={isExpanded}>
              <CardContent className={classes.ruleContent}>
                {/* Filters Section - Only show in on_adjustment mode (when scenarioId exists) */}
                {scenarioId && (
                  <>
                    <Typography className={classes.sectionLabel}>
                      {t("scope_filters")}
                    </Typography>

                    <Box style={{ marginBottom: 16, position: "relative" }}>
                      <FilterSystem
                        dimensions={filterDimensions}
                        filterOptions={filterOptions}
                        selectedFilters={rule.filters || {}}
                        onFiltersChange={(newFilters) =>
                          handleUpdateRule(index, "filters", newFilters)
                        }
                        clientColor={clientColor}
                        disabled={
                          loadingFilters ||
                          Object.keys(filterOptions).length === 0
                        }
                      />
                      {/* Show "All records" when no filters selected - positioned next to filter button */}
                      {(!rule.filters ||
                        Object.keys(rule.filters).length === 0 ||
                        !Object.values(rule.filters).some(
                          (f) => f && f.length > 0,
                        )) && (
                        <Typography
                          variant="body2"
                          style={{
                            position: "absolute",
                            top: "10px",
                            left: "60px", // Right after filter button
                            fontSize: "0.85rem",
                            color: grayColor[6],
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t("all_records")}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}

                {/* Parameter, Operation & Value - All in one row */}
                <Box className={classes.operationRow}>
                  {/* Parameter Selection */}
                  <FormControl size="small" className={classes.operationSelect}>
                    <InputLabel id={`param-label-${index}`}>
                      {t("parameter")}
                    </InputLabel>
                    <Select
                      labelId={`param-label-${index}`}
                      value={rule.parameter}
                      onChange={(e) =>
                        handleUpdateRule(index, "parameter", e.target.value)
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.15)",
                          },
                        },
                      }}
                    >
                      {availableParameters.map((param) => (
                        <MenuItem key={param.parameter} value={param.parameter}>
                          {param.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Operation Selection */}
                  <FormControl size="small" className={classes.operationSelect}>
                    <InputLabel id={`operation-label-${index}`}>
                      {t("operation")}
                    </InputLabel>
                    <Select
                      labelId={`operation-label-${index}`}
                      value={rule.operation}
                      onChange={(e) =>
                        handleUpdateRule(index, "operation", e.target.value)
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.15)",
                          },
                        },
                      }}
                    >
                      {paramOperations.includes("increment_percentage") && (
                        <MenuItem value="increment_percentage">
                          {t("operations.increment_percentage")}
                        </MenuItem>
                      )}
                      {paramOperations.includes("increment_nominal") && (
                        <MenuItem value="increment_nominal">
                          {t("operations.increment_nominal")}
                        </MenuItem>
                      )}
                      {paramOperations.includes("set") && (
                        <MenuItem value="set">{t("operations.set")}</MenuItem>
                      )}
                    </Select>
                  </FormControl>

                  {/* Show value input only if NOT in expand mode */}
                  {!expandByDimension[index] && (
                    <>
                      <TextField
                        type="number"
                        size="small"
                        label={t("value")}
                        value={rule.value}
                        onChange={(e) =>
                          handleUpdateRule(
                            index,
                            "value",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        className={classes.valueInput}
                        inputProps={{
                          step:
                            rule.operation === "increment_percentage" ? 0.1 : 1,
                        }}
                      />

                      {rule.operation === "increment_percentage" && (
                        <Typography variant="body2" color="textSecondary">
                          %
                        </Typography>
                      )}
                    </>
                  )}
                </Box>

                {/* Expand by Dimension Section - Only show in on_adjustment mode (when scenarioId exists) */}
                {scenarioId && (
                  <Box style={{ marginTop: 16 }}>
                    {/* Checkbox and Dimension Dropdown - Horizontal Layout */}
                    <Box
                      style={{ display: "flex", alignItems: "center", gap: 16 }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={expandByDimension[index] || false}
                            onChange={(e) =>
                              handleToggleExpandByDimension(
                                index,
                                e.target.checked,
                              )
                            }
                            size="small"
                            style={{
                              color: rule.parameter
                                ? clientColor
                                : grayColor[6],
                            }}
                            disabled={!rule.parameter}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            style={{
                              fontSize: "0.85rem",
                              color: !rule.parameter ? grayColor[6] : "inherit",
                            }}
                          >
                            {t("expand_by_dimension")}
                          </Typography>
                        }
                      />

                      {/* Dimension Dropdown - shown when expand is checked - inline to the right */}
                      {expandByDimension[index] && (
                        <FormControl
                          size="small"
                          style={{ minWidth: 200 }}
                          className={classes.selectorControl}
                        >
                          <InputLabel id={`expand-dim-label-${index}`}>
                            {t("dimension")}
                          </InputLabel>
                          <Select
                            labelId={`expand-dim-label-${index}`}
                            value={selectedExpandDimension[index] || ""}
                            onChange={(e) =>
                              handleExpandDimensionSelect(
                                index,
                                e.target.value,
                                scenarioId,
                              )
                            }
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 300,
                                  boxShadow:
                                    "0px 5px 15px 0px rgba(0, 0, 0, 0.15)",
                                },
                              },
                            }}
                          >
                            {filterDimensions.map((dim) => (
                              <MenuItem key={dim.key} value={dim.key}>
                                {dim.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>

                    {/* Value Table - shown when dimension is selected - Below on its own line */}
                    {expandByDimension[index] &&
                      selectedExpandDimension[index] && (
                        <Box style={{ marginTop: 16 }}>
                          {loadingDimensionValues[index] ? (
                            <Box display="flex" justifyContent="center" p={2}>
                              <ThreeDots
                                height="30"
                                width="30"
                                radius="6"
                                color={clientColor}
                                ariaLabel="three-dots-loading"
                                visible={true}
                              />
                            </Box>
                          ) : (
                            <>
                              <Table
                                size="small"
                                style={{ border: `1px solid ${grayColor[8]}` }}
                              >
                                <TableHead>
                                  <TableRow
                                    style={{ backgroundColor: grayColor[12] }}
                                  >
                                    <TableCell
                                      style={{
                                        fontWeight: 600,
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      {
                                        filterDimensions.find(
                                          (d) =>
                                            d.key ===
                                            selectedExpandDimension[index],
                                        )?.label
                                      }
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontWeight: 600,
                                        fontSize: "0.8rem",
                                        width: "1%",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Current{" "}
                                      {(() => {
                                        // Get parameter label to extract unit
                                        const paramConfig =
                                          availableParameters.find(
                                            (p) =>
                                              p.parameter === rule.parameter,
                                          );
                                        const label = paramConfig?.label || "";
                                        // Check if label contains (%) - common pattern
                                        if (
                                          label.includes("(%)") ||
                                          label.includes("%")
                                        ) {
                                          return "(%)";
                                        }
                                        return ""; // For price/nominal values
                                      })()}
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontWeight: 600,
                                        fontSize: "0.8rem",
                                        width: "1%",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Adjustment{" "}
                                      {rule.operation === "increment_percentage"
                                        ? "(%)"
                                        : ""}
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontWeight: 600,
                                        fontSize: "0.8rem",
                                        width: "1%",
                                        whiteSpace: "nowrap",
                                        minWidth: "120px",
                                      }}
                                    >
                                      Preview
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(() => {
                                    const values = dimensionValues[index] || [];
                                    const page = currentPage[index] || 1;
                                    const startIdx =
                                      (page - 1) * ITEMS_PER_PAGE;
                                    const endIdx = startIdx + ITEMS_PER_PAGE;
                                    const paginatedValues = values.slice(
                                      startIdx,
                                      endIdx,
                                    );
                                    const totalPages = Math.ceil(
                                      values.length / ITEMS_PER_PAGE,
                                    );

                                    return (
                                      <>
                                        {paginatedValues.map((dimVal) => {
                                          const aggregates =
                                            dimensionAggregates[index] || {};
                                          const aggData =
                                            aggregates[dimVal] || {};
                                          const currentAvg = aggData.avg;
                                          const userInput =
                                            dimensionValueInputs[index]?.[
                                              dimVal
                                            ];

                                          // Calculate preview based on operation
                                          let preview = null;
                                          if (
                                            currentAvg !== null &&
                                            currentAvg !== undefined &&
                                            userInput !== undefined &&
                                            userInput !== ""
                                          ) {
                                            const inputVal =
                                              parseFloat(userInput) || 0;
                                            if (
                                              rule.operation ===
                                              "increment_percentage"
                                            ) {
                                              preview =
                                                currentAvg *
                                                (1 + inputVal / 100);
                                            } else if (
                                              rule.operation ===
                                              "increment_nominal"
                                            ) {
                                              preview = currentAvg + inputVal;
                                            } else if (
                                              rule.operation === "set"
                                            ) {
                                              preview = inputVal;
                                            }
                                          }

                                          return (
                                            <TableRow key={dimVal}>
                                              {/* Dimension Value */}
                                              <TableCell
                                                style={{ fontSize: "0.8rem" }}
                                              >
                                                {dimVal}
                                              </TableCell>

                                              {/* Current Avg with Warning Icon */}
                                              <TableCell
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                <Box
                                                  display="flex"
                                                  alignItems="center"
                                                  gap={0.5}
                                                >
                                                  <Typography
                                                    style={{
                                                      fontSize: "0.8rem",
                                                      color: grayColor[6],
                                                    }}
                                                  >
                                                    {currentAvg !== null &&
                                                    currentAvg !== undefined
                                                      ? currentAvg.toFixed(2)
                                                      : "-"}
                                                  </Typography>
                                                  {aggData.has_variance && (
                                                    <WarningIcon
                                                      onClick={(e) =>
                                                        handleVariancePopoverOpen(
                                                          e,
                                                          index,
                                                          dimVal,
                                                        )
                                                      }
                                                      style={{
                                                        fontSize: "0.9rem",
                                                        color: "#ff9800",
                                                        cursor: "pointer",
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                              </TableCell>

                                              {/* Adjustment Input */}
                                              <TableCell
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                <TextField
                                                  type="number"
                                                  size="small"
                                                  value={userInput ?? ""}
                                                  onChange={(e) =>
                                                    handleDimensionValueChange(
                                                      index,
                                                      dimVal,
                                                      e.target.value,
                                                    )
                                                  }
                                                  className={
                                                    classes.dimensionValueInput
                                                  }
                                                  inputProps={{
                                                    step:
                                                      rule.operation ===
                                                      "increment_percentage"
                                                        ? 0.1
                                                        : 1,
                                                    style: {
                                                      fontSize: "0.8rem",
                                                    },
                                                  }}
                                                  placeholder=" - "
                                                />
                                              </TableCell>

                                              {/* Preview */}
                                              <TableCell
                                                style={{
                                                  whiteSpace: "nowrap",
                                                  minWidth: "180px",
                                                }}
                                              >
                                                {preview !== null ? (
                                                  <Typography
                                                    variant="body2"
                                                    style={{
                                                      fontSize: "0.8rem",
                                                      color: grayColor[6],
                                                      fontFamily: "monospace",
                                                    }}
                                                  >
                                                    {rule.operation ===
                                                      "increment_percentage" && (
                                                      <>
                                                        {currentAvg.toFixed(2)}{" "}
                                                        ×{" "}
                                                        {(
                                                          1 +
                                                          (parseFloat(
                                                            userInput,
                                                          ) || 0) /
                                                            100
                                                        ).toFixed(2)}{" "}
                                                        = {preview.toFixed(2)}
                                                      </>
                                                    )}
                                                    {rule.operation ===
                                                      "increment_nominal" && (
                                                      <>
                                                        {currentAvg.toFixed(2)}{" "}
                                                        {parseFloat(
                                                          userInput,
                                                        ) >= 0
                                                          ? "+"
                                                          : "−"}{" "}
                                                        {Math.abs(
                                                          parseFloat(
                                                            userInput,
                                                          ) || 0,
                                                        ).toFixed(2)}{" "}
                                                        = {preview.toFixed(2)}
                                                      </>
                                                    )}
                                                    {rule.operation ===
                                                      "set" && (
                                                      <>
                                                        = {preview.toFixed(2)}
                                                      </>
                                                    )}
                                                  </Typography>
                                                ) : (
                                                  <Typography
                                                    variant="body2"
                                                    style={{
                                                      fontSize: "0.8rem",
                                                      color: grayColor[8],
                                                    }}
                                                  >
                                                    -
                                                  </Typography>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                        {/* Pagination Controls */}
                                        {totalPages > 1 && (
                                          <TableRow>
                                            <TableCell
                                              colSpan={4}
                                              style={{
                                                textAlign: "center",
                                                padding: "8px",
                                              }}
                                            >
                                              <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                gap={1}
                                              >
                                                <IconButton
                                                  size="small"
                                                  disabled={page === 1}
                                                  onClick={() =>
                                                    setCurrentPage({
                                                      ...currentPage,
                                                      [index]: page - 1,
                                                    })
                                                  }
                                                >
                                                  <ChevronLeftIcon />
                                                </IconButton>
                                                <Typography
                                                  variant="body2"
                                                  style={{
                                                    fontSize: "0.75rem",
                                                  }}
                                                >
                                                  {t("page_of", {
                                                    page,
                                                    total: totalPages,
                                                  })}
                                                </Typography>
                                                <IconButton
                                                  size="small"
                                                  disabled={page === totalPages}
                                                  onClick={() =>
                                                    setCurrentPage({
                                                      ...currentPage,
                                                      [index]: page + 1,
                                                    })
                                                  }
                                                >
                                                  <ChevronRightIcon />
                                                </IconButton>
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </>
                                    );
                                  })()}
                                </TableBody>
                              </Table>
                            </>
                          )}
                        </Box>
                      )}
                  </Box>
                )}
              </CardContent>
            </Collapse>
          </Card>
        );
      })}

      {/* Variance Popover */}
      <Popover
        open={Boolean(variancePopover.anchorEl)}
        anchorEl={variancePopover.anchorEl}
        onClose={handleVariancePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          style: {
            maxWidth: 700,
            padding: 16,
          },
        }}
      >
        {variancePopover.ruleIndex !== null &&
          variancePopover.dimVal !== null &&
          (() => {
            const ruleIndex = variancePopover.ruleIndex;
            const dimVal = variancePopover.dimVal;
            const aggData =
              (dimensionAggregates[ruleIndex] || {})[dimVal] || {};
            const rawSamples =
              (dimensionSamples[ruleIndex] || {})[dimVal] || [];

            // Handle both old format (array of numbers) and new format (array of objects)
            const samples = rawSamples.map((item) => {
              if (typeof item === "number") {
                // Old format: convert number to object
                return { raw_value: item };
              }
              return item; // New format: already an object
            });

            // Get the selected dimension for this rule to label the first column
            const selectedDimension = selectedExpandDimension[ruleIndex];
            const dimensionConfig = availableDimensions.find(
              (d) => d.id === selectedDimension,
            );
            const dimensionLabel =
              dimensionConfig?.label || selectedDimension || "Dimension";

            // Column labels for display
            const columnLabels = {
              dimension_value: dimensionLabel,
              sku_id: t("variance_popover.columns.sku_id", "Product ID"),
              sku_name: t("variance_popover.columns.sku_name", "Product"),
              sku_label_imported: t(
                "variance_popover.columns.sku_label_imported",
                "SKU Label",
              ),
              channel_reference: t(
                "variance_popover.columns.channel_reference",
                "Channel",
              ),
              region_reference: t(
                "variance_popover.columns.region_reference",
                "Region",
              ),
              node1_reference_productionsite: t(
                "variance_popover.columns.node1_reference",
                "Plant",
              ),
              node2_reference_client: t(
                "variance_popover.columns.node2_reference",
                "Client",
              ),
              node3_reference_pointofsale: t(
                "variance_popover.columns.node3_reference",
                "POS",
              ),
              node1_location: t(
                "variance_popover.columns.node1_location",
                "Plant State",
              ),
              node2_location: t(
                "variance_popover.columns.node2_location",
                "Client State",
              ),
              node3_location: t(
                "variance_popover.columns.node3_location",
                "POS State",
              ),
              raw_value: t("variance_popover.columns.raw_value", "Value"),
            };

            // Determine which OTHER columns have varying values (excluding dimension_value and raw_value)
            const getVaryingOtherColumns = (sampleRows) => {
              if (!sampleRows || sampleRows.length <= 1) return [];

              const excludeColumns = ["dimension_value", "raw_value"];
              const allColumns = Object.keys(sampleRows[0] || {}).filter(
                (col) => !excludeColumns.includes(col),
              );

              return allColumns.filter((col) => {
                const values = sampleRows.map((row) => row[col]);
                // Normalize values for comparison (handle null/undefined/empty string as equivalent)
                const normalizedValues = values.map((v) =>
                  v === null || v === undefined || v === "" ? null : v,
                );
                const uniqueValues = new Set(normalizedValues);
                return uniqueValues.size > 1;
              });
            };

            // Build final column order: dimension_value (first), raw_value (second), then only varying columns
            const varyingOtherColumns = getVaryingOtherColumns(samples);
            const columnsToShow = [
              "dimension_value",
              "raw_value",
              ...varyingOtherColumns,
            ];

            // Format cell value for display
            const formatCellValue = (value, col) => {
              if (value === null || value === undefined) return "-";
              if (col === "raw_value" && typeof value === "number") {
                return value.toFixed(4);
              }
              return String(value);
            };

            return (
              <Box>
                {/* Section 1: Explanation */}
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 600, marginBottom: 8 }}
                >
                  {t("variance_popover.explanation_title")}
                </Typography>
                <Typography
                  variant="body2"
                  style={{ color: grayColor[6], marginBottom: 12 }}
                >
                  {t("variance_popover.explanation_text")}
                </Typography>

                <Divider style={{ marginBottom: 12 }} />

                {/* Section 2: Raw Data Table */}
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 600, marginBottom: 8 }}
                >
                  {t("variance_popover.raw_data_title")}
                </Typography>
                {samples.length > 0 ? (
                  <Box
                    style={{
                      maxHeight: 300,
                      overflowY: "auto",
                      marginBottom: 12,
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {columnsToShow.map((col) => (
                            <TableCell
                              key={col}
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                padding: "4px 8px",
                                whiteSpace: "nowrap",
                                backgroundColor: "#fafafa",
                              }}
                            >
                              {columnLabels[col] || col}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {samples.map((row, idx) => (
                          <TableRow key={idx}>
                            {columnsToShow.map((col) => (
                              <TableCell
                                key={col}
                                style={{
                                  fontSize: "0.7rem",
                                  padding: "4px 8px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatCellValue(row[col], col)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    style={{ color: grayColor[6], marginBottom: 12 }}
                  >
                    {t("variance_popover.no_samples")}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  style={{
                    color: grayColor[6],
                    display: "block",
                    marginBottom: 12,
                  }}
                >
                  {t("variance_popover.range_info", {
                    min: aggData.min?.toFixed(2) || "-",
                    max: aggData.max?.toFixed(2) || "-",
                  })}
                </Typography>
              </Box>
            );
          })()}
      </Popover>

      {/* Add Rule Button */}
      <Button
        variant="outlined"
        onClick={handleAddRule}
        className={classes.addRuleButton}
        disabled={requireScenario && !scenarioId}
      >
        {t("add_rule")}
      </Button>
    </Box>
  );
};

MaskRulesBuilder.propTypes = {
  rules: PropTypes.arrayOf(
    PropTypes.shape({
      parameter: PropTypes.string,
      filters: PropTypes.object,
      operation: PropTypes.string,
      value: PropTypes.number,
      expand_by_dimension: PropTypes.string,
      dimension_values: PropTypes.object,
    }),
  ),
  onChange: PropTypes.func.isRequired,
  config: PropTypes.shape({
    dimensions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
      }),
    ),
    parameters: PropTypes.arrayOf(
      PropTypes.shape({
        parameter: PropTypes.string,
        label: PropTypes.string,
        operations: PropTypes.arrayOf(PropTypes.string),
      }),
    ),
  }),
  filterOptions: PropTypes.object,
  loadingFilters: PropTypes.bool,
  scenarioId: PropTypes.number,
  requireScenario: PropTypes.bool,
};

export default MaskRulesBuilder;
